import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Strings from '../../../Strings';
import { actions } from '../../SuperUser/Patients/redux/actions';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import {
  DELETE_CUSTOM_TASK_RESULT,
  GET_TASKS_LIST_ALL_ORGLESS_RESULT,
  UPDATE_GLOBAL_TASK_DEFAULTS_RESULT,
} from '../../SuperUser/Patients/redux/constants';
import { showStatus } from '../../../utils';
import { ActionDropdownOnOffMenu, ActionWidget } from '../../../components/ActionWidget';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { openModalAction } from '../../../actions/modal';
import { CUSTOM_WORK_TYPE_MODAL_NAME } from '../../../modals/CustomWorkTypeModal';
import { PageHeader } from '../../../components/PageHeader';
import { TextInput } from '../../../components/PageHeader/TextInput';

function WorkTypes(props) {
  const { getAllTasks, deleteCustomTask, updateGlobalTaskDefaults, openCustomWorkTypeModal, showNotification } = props;
  const [allTasks, setAllTasks] = React.useState([]);
  const [selectedTasks, setSelectedTasks] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const savingRef = React.useRef(false);

  const tableRef = React.useRef(null);

  const load = React.useCallback(() => {
    getAllTasks().then(resp => {
      if (resp?.type === GET_TASKS_LIST_ALL_ORGLESS_RESULT) {
        setAllTasks(resp?.response?.data || []);
      }
    });
    tableRef?.current?.resetSelectedRows();
  }, [getAllTasks]);

  React.useEffect(() => {
    load();
  }, [load]);

  const changeTasksValue = React.useCallback(async (taskIds, value) => {
    if (savingRef.current || taskIds.length === 0) return;
    const selected = allTasks.filter(task => taskIds.includes(task.id));
    if (selected.every(task => task.default === (value === 1))) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const resp = await updateGlobalTaskDefaults({task_ids: taskIds, default: value === 1});
      if (resp?.type === UPDATE_GLOBAL_TASK_DEFAULTS_RESULT) {
        showNotification(Strings.workTypeDefaultsUpdated);
        load();
      } else {
        showNotification(resp?.response?.data?.standard_error?.message || Strings.unableToUpdateWorkTypeDefaults, 5000, true);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [allTasks, updateGlobalTaskDefaults, showNotification, load]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('id', {
      header: Strings.id,
      size: 30,
    }),
    columnHelper.accessor('taskName', {
      header: Strings.capPatient.taskName,
      size: 500,
    }),
    columnHelper.accessor('default', {
      header: () => (
        <span data-tooltip-id="tooltip" data-tooltip-content={Strings.globalTaskDefaultExplanation}>
          {Strings.globalTaskDefault}
        </span>
      ),
      cell: info => showStatus(!!info.getValue()),
      size: 100,
    }),
    columnHelper.accessor('allowDuration', {
      header: Strings.tracksTime,
      cell: info => showStatus(info.getValue() !== false),
      size: 100,
    }),
  ], []);
  
  const dataForTable = React.useMemo(() => {
    return allTasks
      .filter(task => (task.taskName || '').toLowerCase().includes(searchQuery.trim().toLowerCase()))
      .sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
        return (a.taskName || '').localeCompare(b.taskName || '');
      });
  }, [allTasks, searchQuery]);

  const handleRowSelectionChange = React.useCallback((rows) => {
    setSelectedTasks(rows.map(r => r.id));
  }, []);

  const openCreateTaskModal = React.useCallback(() => {
    openCustomWorkTypeModal({
      mode: 'create',
      orgId: null,
      allTasks,
      onSuccess: load,
    });
  }, [openCustomWorkTypeModal, load]);

  const openEditTaskModal = React.useCallback((taskRow) => {
    openCustomWorkTypeModal({
      mode: 'editGenericTask',
      orgId: null,
      allTasks,
      task: taskRow,
      onSuccess: load,
    });
  }, [openCustomWorkTypeModal, allTasks, load]);

  const selectedRows = allTasks.filter(task => selectedTasks.includes(task.id));
  const allDefaultsEnabled = selectedRows.length > 0 && selectedRows.every(task => task.default);
  const allDefaultsDisabled = selectedRows.length > 0 && selectedRows.every(task => !task.default);

  const actionBtns = React.useMemo(() => (
    <ActionDropdownOnOffMenu
      img="action-edit"
      tooltiptext={Strings.globalTaskDefault}
      off={{
        label: Strings.disableTaskByDefault,
        tooltip: Strings.globalTaskDefaultExplanation,
        disabled: allDefaultsDisabled,
        disabledReason: allDefaultsDisabled ? Strings.allSelectedDefaultsDisabled : undefined,
      }}
      on={{
        label: Strings.enableTaskByDefault,
        tooltip: Strings.globalTaskDefaultExplanation,
        disabled: allDefaultsEnabled,
        disabledReason: allDefaultsEnabled ? Strings.allSelectedDefaultsEnabled : undefined,
      }}
      disabled={isSaving}
      action={value => changeTasksValue(selectedTasks, value)}
    />
  ), [changeTasksValue, selectedTasks, allDefaultsEnabled, allDefaultsDisabled, isSaving]);

  const deleteRestriction = task => [
    task.mandatoryOrganizations?.length > 0
      ? Strings.formatString(Strings.workTypeMandatoryOrganizations, task.mandatoryOrganizations.map(org => org.name).join(', ')) : '',
    task.autoStartOrganizations?.length > 0
      ? Strings.formatString(Strings.workTypeAutoStartOrganizations, task.autoStartOrganizations.map(org => org.name).join(', ')) : '',
  ].filter(Boolean).join(' ');

  const tableButtons = React.useMemo(() => [
    {
      icon: 'edit',
      onClick: (_, row) => openEditTaskModal(row),
      text: Strings.edit,
    },
    {
      icon: 'delete',
      disabled: (_, row) => row.deletable !== true,
      disabledTooltip: (_, row) => deleteRestriction(row),
      onClick: (_, row) => {
        if (row.deletable !== true) return;
        deleteCustomTask({ task_id: row.id }).then(resp => {
          if (resp?.type === DELETE_CUSTOM_TASK_RESULT) {
            showNotification(Strings.workTypeDeleted);
            load();
          } else {
            showNotification(resp?.response?.data?.standard_error?.message || Strings.unableToDeleteWorkType, 5000, true);
          }
        });
      },
      text: Strings.delete,
    },
  ], [openEditTaskModal, deleteCustomTask, showNotification, load]);

  return (
    <>
      <PageHeader
        left={Strings.workTypes}
        right={(
          <>
            <TextInput
              placeholder={Strings.search}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              onClick={openCreateTaskModal}
              title={Strings.add}
            />
          </>
        )}
      />
      <Table3
        data={dataForTable}
        columns={columns}
        name="WorkTypes"
        buttons={tableButtons}
        enableColumnFiltering
        pinFirstColumn
        enableRowSelection
        onRowSelectionChange={handleRowSelectionChange}
        ref={tableRef}
      />
      <ActionWidget show={selectedTasks?.length > 0}>{actionBtns}</ActionWidget>
    </>
  );
}

WorkTypes.propTypes = {
  getAllTasks: PropTypes.func.isRequired,
  getTasks: PropTypes.func.isRequired,
  updateGlobalTaskDefaults: PropTypes.func.isRequired,
  deleteCustomTask: PropTypes.func.isRequired,
  openCustomWorkTypeModal: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = {
  getAllTasks: actions.getAllTasksOrgless,
  getTasks: actions.getTasks,
  deleteCustomTask: actions.deleteCustomTask,
  updateGlobalTaskDefaults: actions.updateGlobalTaskDefaults,
  openCustomWorkTypeModal: data => openModalAction(CUSTOM_WORK_TYPE_MODAL_NAME, data),
  showNotification: notificationActions.show,
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkTypes);
